/**
 * The ONE place that turns the raw rows behind a booking — payments, their
 * proofs, submitted handover protocols, the deposit settlement — into the
 * `TenantJourneySignals` the stage resolver consumes.
 *
 * Why this module exists: the derived stage is only as consistent as its
 * inputs. `resolveTenantJourney` is already shared, but each surface used to
 * assemble the signals object by hand, and every surface that assembled it
 * slightly differently (or passed `false` because it had not loaded a table)
 * showed the same tenancy at a different stage. The tenant list and the tenant
 * profile were reconciled once by copying the same expression into both; the
 * room view was never reconciled at all and still rendered a hardcoded
 * "Active" badge.
 *
 * So the assembly lives here, once. A surface supplies the rows it has and gets
 * the stage the tenant profile would show — it cannot accidentally invent a
 * different definition of "deposit paid" or "nothing to refund".
 *
 * @see src/lib/tenantJourney.ts — the stage ladder and its gates.
 * @see src/hooks/v2/useTenantJourneys.ts — loads these rows for any surface.
 */
import {
  resolveTenantJourney,
  type TenantJourney,
  type TenantJourneySignals,
} from "@/lib/tenantJourney";
import { getExpectedDepositEUR, isDepositFullyPaid } from "@/lib/rent";
import { resolveNoDepositHeld } from "@/lib/depositSettlement";

/** The settlement columns the journey needs — nothing more is loaded for it. */
export interface DepositSettlementLite {
  refund_paid_at?: string | null;
  deposit_held_cents?: number | null;
  amounts_snapshot_at?: string | null;
}

export interface TenantJourneySignalSources {
  /** All payment rows for the booking (any type — deposits are filtered out). */
  payments: any[];
  /** Latest proof per payment id, for the manual-payment rule. */
  proofFor: (
    paymentId: string
  ) => { status?: string | null } | null | undefined;
  /** Kinds ("move_in" / "move_out") of the booking's SUBMITTED protocols. */
  submittedProtocolKinds?: ReadonlySet<string> | null;
  /** The booking's deposit settlement, when one exists. */
  settlement?: DepositSettlementLite | null;
}

/**
 * Assemble the resolver's signals from the rows a surface has loaded.
 *
 * Every absent source reads as "no evidence", which is the conservative
 * direction: the tenant parks at the earlier stage rather than being promoted
 * past a gate nobody proved.
 */
export function tenantJourneySignalsFrom(
  booking: any,
  sources: TenantJourneySignalSources
): TenantJourneySignals {
  const settlement = sources.settlement ?? null;
  return {
    // The canonical FULL-deposit resolver, not "some deposit row is paid" — a
    // part-payment must not clear the deposit gate.
    depositPaid: isDepositFullyPaid({
      booking,
      payments: sources.payments ?? [],
      proofFor: sources.proofFor,
    }),
    moveInProtocolSubmitted: !!sources.submittedProtocolKinds?.has("move_in"),
    moveOutProtocolSubmitted: !!sources.submittedProtocolKinds?.has("move_out"),
    // Both halves: released AND the money actually moved.
    depositRefundSettled: !!settlement?.refund_paid_at,
    noDepositHeld: resolveNoDepositHeld({
      snapshotAt: settlement?.amounts_snapshot_at,
      snapshotHeldCents: settlement?.deposit_held_cents,
      expectedCents: Math.round(getExpectedDepositEUR(booking) * 100),
    }),
  };
}

/** `resolveTenantJourney` fed by {@link tenantJourneySignalsFrom}. */
export function resolveTenantJourneyFromSources(
  booking: any,
  sources: TenantJourneySignalSources
): TenantJourney {
  return resolveTenantJourney(
    booking,
    tenantJourneySignalsFrom(booking, sources)
  );
}

/** A move-in was recorded (`move_in_confirmed_at`, or the legacy `actual_move_in_date`). */
export function hasRecordedMoveIn(booking: any): boolean {
  return !!(booking?.move_in_confirmed_at || booking?.actual_move_in_date);
}

/**
 * Did this booking ever become a real tenancy — did someone actually live there?
 *
 * Two signs, either is enough:
 *   1. a recorded move-in (`move_in_confirmed_at`, or the legacy
 *      `actual_move_in_date`). Applicants never carry one, and it survives
 *      deposit data that is incomplete or predates the proof/attestation rules —
 *      a real tenant must not vanish from a room's history over that.
 *   2. the deposit was paid — the commitment that takes a room off the market.
 *      Same canonical full-deposit rule as the journey's "Security deposit paid
 *      in full" gate, with one deliberate difference: {@link isDepositFullyPaid}
 *      is vacuously true when nothing is owed, which would count every applicant
 *      with no rent or deposit set. Nothing owed and no move-in is not a tenancy.
 *
 * Not a signed contract and not an archive flag: neither says anyone was there.
 */
export function bookingWasRealTenancy(
  booking: any,
  sources: Pick<TenantJourneySignalSources, "payments" | "proofFor">
): boolean {
  if (hasRecordedMoveIn(booking)) return true;
  if (getExpectedDepositEUR(booking) <= 0) return false;
  return isDepositFullyPaid({
    booking,
    payments: sources.payments ?? [],
    proofFor: sources.proofFor,
  });
}
